import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../kit/Sheet';
import { Button } from '../kit/Button';
import { haptic } from '../../lib/haptic';
import { formatISTTime, istDateYmd } from '../../lib/format';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { startKm: number | null; actualStartDate: string; actualStartTime: string }) => void;
  submitting?: boolean;
  error?: string | null;
};

export function StartTripSheet({ open, onClose, onConfirm, submitting, error }: Props) {
  const { t } = useTranslation('trip');
  const [km, setKm] = useState('');
  // Start KM is OPTIONAL (2026-07-02 lifecycle): a driver already rolling can
  // start without it and add it from the in-trip banner. Empty = skip; a
  // non-empty value must still look like an odometer.
  const isValid = km === '' || /^\d{3,6}$/.test(km);

  const handleSubmit = () => {
    if (!isValid) return;
    haptic.tap();
    const now = new Date();
    onConfirm({
      startKm: km === '' ? null : parseInt(km, 10),
      actualStartDate: istDateYmd(now),
      actualStartTime: formatISTTime(now),
    });
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('start_trip_sheet_title', { defaultValue: 'Start trip' })}>
      <div className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
            {t('start_km_label', { defaultValue: 'Start KM' })}
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={km}
            onChange={(e) => setKm(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            className="w-full px-4 py-3 text-2xl text-center font-mono tracking-widest bg-[var(--color-surface-soft)] border-2 border-[var(--color-border,#d1d5db)] rounded-2xl focus:border-[var(--green-700,#1B6A41)] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            autoFocus
          />
          <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
            {t('start_km_optional_hint', { defaultValue: 'Optional — you can add it later from the trip screen.' })}
          </p>
        </label>
        {error ? <p role="alert" className="text-sm text-[var(--color-danger,#C13834)]">{error}</p> : null}
        <Button onClick={handleSubmit} disabled={!isValid || submitting} loading={submitting} className="w-full">
          {t('start_trip_confirm', { defaultValue: 'Start trip' })}
        </Button>
      </div>
    </Sheet>
  );
}
